import type { TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import type { IPageInfo } from '@iharbeck/ngx-virtual-scroller';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  filter,
  map,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';

import { debounceAsap, filterBoolean } from '@core/common-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import type { Select2Item } from '@core/user-interface-legacy';
import {
  AlwaysOpenStrategyDirective,
  CheckboxComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  DropdownWidth,
  HeaderDefaultComponent,
  IconButtonComponent,
  IconComponent,
  MultiselectFeatureDirective,
  MultiSelectListItemComponent,
  NestedTemplateTriggerDirective,
  RtTooltip,
  SearchFeatureDirective,
  Select2Component,
  Select2HeaderTemplateDirective,
  Select2TriggerDirective,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import { ViewGroupsSelectors } from '@core/views-data';
import type { AnyGroup, Grouping, ViewForm } from '@core/views-types';

import { ViewsAnalytics } from '../../../analytics/views-analytics';
import { ViewDataService } from '../../view-data.service';

@Component({
  selector: 'view-settings-groups',
  imports: [
    CheckboxComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    HeaderDefaultComponent,
    IconButtonComponent,
    IconComponent,
    MultiSelectListItemComponent,
    MultiselectFeatureDirective,
    SearchFeatureDirective,
    Select2Component,
    Select2TriggerDirective,
    TranslatePipe,
    ReactiveFormsModule,
    Select2HeaderTemplateDirective,
    NestedTemplateTriggerDirective,
    RtTooltip,
    AlwaysOpenStrategyDirective,
  ],
  templateUrl: './view-settings-groups.component.html',
  styleUrl: './view-settings-groups.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewSettingsGroupsComponent {
  private readonly GROUP_LOAD_LIMIT = 10;
  protected readonly DropdownWidth = DropdownWidth;

  public groupControl = input.required<ViewForm['controls']['group']>();
  public layoutControl = input.required<ViewForm['controls']['layout']>();
  public defaultGroup = input.required<string>();
  public groups = input.required<Grouping[]>();
  public hasBackButton = input.required<boolean>();

  public groupingSelectTmpl = viewChild.required<TemplateRef<void>>('groupingSelectTmpl');
  public groupingTmpl = viewChild.required<TemplateRef<void>>('groupingTmpl');

  public groupingItems: Select2Item[] = [];
  public selectedGrouping: Grouping | null = null;
  public groupItems: Select2Item[] = [];
  public groupSearch = new FormControl('');
  public groupsSelectAllState: 'all' | 'none' | 'indeterminate' = 'all';
  public groupScrollEnd = new Subject<IPageInfo>();

  public mainDropdown = inject(DropdownComponent);
  public viewsAnalytics = inject(ViewsAnalytics);

  private hiddenGroupsStoreId = 'hidden-groups';
  private store = inject(Store);
  private viewDataService = inject(ViewDataService);
  private cdRef = inject(ChangeDetectorRef);
  private untilDestroyed = injectUntilDestroyed();
  private translate = injectTranslate();

  constructor() {
    effect(() => {
      this.selectedGrouping = this.getSelectedGroup();
      this.groupingItems = this.getGroupingItems();
    });
  }

  public ngOnInit() {
    this.watchGrouping();
    this.watchLayout();
  }

  public onGroupsSelectAllClick() {
    const { hidden: hiddenGroupsControl } = this.groupControl().controls;
    if (this.groupsSelectAllState === 'indeterminate' || this.groupsSelectAllState === 'none') {
      this.groupsSelectAllState = 'all';
      hiddenGroupsControl.setValue([]);
      this.cdRef.detectChanges();
    } else {
      this.groupsSelectAllState = 'none';
      const groupsState = this.store.selectSignal(
        ViewGroupsSelectors.byId(this.hiddenGroupsStoreId),
      )();
      const allGroupsLoaded =
        !!groupsState && groupsState.meta.totalCount === groupsState.groups.length;
      if (allGroupsLoaded) {
        return this.setHiddenGroups(groupsState.groups);
      }
      const loadRef = this.viewDataService.loadAllGroups(
        this.hiddenGroupsStoreId,
        this.groupSearch.value!,
      );
      if (!loadRef) return;

      loadRef
        .pipe(filterBoolean, take(1))
        .subscribe(({ payload: { data } }) => this.setHiddenGroups(data.groups));
      this.cdRef.detectChanges();
    }
  }

  public onHideEmptyGroupsClick() {
    if (!this.selectedGrouping) return;

    const { hideEmpty, configId } = this.groupControl().controls;
    const nextValue = !hideEmpty.value;
    hideEmpty.setValue(nextValue);
    this.viewsAnalytics.settingsHideEmptyGroupsToggle({
      groupBy: configId.value!,
      toggleValue: nextValue,
    });
  }

  private watchLayout() {
    const { type: layoutTypeControl } = this.layoutControl().controls;

    layoutTypeControl.valueChanges.pipe(startWith(layoutTypeControl.value)).subscribe(() => {
      this.groupingItems = this.getGroupingItems();
      this.cdRef.detectChanges();
    });
  }

  private watchGrouping() {
    this.handleGroupSearch();
    this.handleGroupScroll();
    this.handleGroupSelectAllState();
  }

  private handleGroupSearch() {
    combineLatest([
      this.groupSearch.valueChanges.pipe(startWith(this.groupSearch.value)),
      this.viewDataService.formParams$,
    ])
      .pipe(debounceAsap(), this.untilDestroyed)
      .subscribe(([search]) => {
        this.selectedGrouping = this.getSelectedGroup();
        this.viewDataService.loadGroups(
          1,
          this.GROUP_LOAD_LIMIT,
          this.hiddenGroupsStoreId,
          search!,
        );
        this.cdRef.detectChanges();
      });
  }

  private handleGroupScroll() {
    this.groupScrollEnd
      .pipe(
        filter(({ endIndexWithBuffer }) => endIndexWithBuffer === this.groupItems.length - 1),
        withLatestFrom(
          this.store.select(ViewGroupsSelectors.isPending(this.hiddenGroupsStoreId)),
          this.store.select(ViewGroupsSelectors.byId(this.hiddenGroupsStoreId)),
        ),
        filter(([_, isPending]) => !isPending),
        map(data => data[2]),
        filterBoolean,
        this.untilDestroyed,
      )
      .subscribe(({ meta }) => {
        if (meta.limit * meta.page >= meta.totalCount) return;

        const nextPage = meta.page + 1;
        this.viewDataService.loadGroups(
          nextPage,
          this.GROUP_LOAD_LIMIT,
          this.hiddenGroupsStoreId,
          this.groupSearch.value!,
        );
      });
  }

  private handleGroupSelectAllState() {
    const { configId: groupingIdControl, hidden: hiddenGroupsControl } =
      this.groupControl().controls;
    combineLatest([
      groupingIdControl.valueChanges.pipe(
        startWith(groupingIdControl.value),
        switchMap(groupingId =>
          this.viewDataService.getGroupingById(this.hiddenGroupsStoreId, groupingId!),
        ),
        tap(([groupingId, groupData]) => {
          this.groupItems = !groupData
            ? []
            : groupData.groups.map(group => ({
                label: group.label,
                value: group.id,
              }));
          this.cdRef.detectChanges();
          this.mainDropdown.forceViewUpdate();
        }),
      ),
      hiddenGroupsControl.valueChanges.pipe(startWith(hiddenGroupsControl.value)),
    ])
      .pipe(debounceAsap(), this.untilDestroyed)
      .subscribe(([[groupingId, groupData], hiddenGroups]) => {
        if (!hiddenGroups.length) {
          this.groupsSelectAllState = 'all';
        } else if (hiddenGroups.length === groupData?.meta.totalCount) {
          this.groupsSelectAllState = 'none';
        } else {
          this.groupsSelectAllState = 'indeterminate';
        }
        this.cdRef.detectChanges();
      });
  }

  private getGroupingItems(): Select2Item[] {
    const items = this.groups().map(group => ({
      label: group.name,
      value: group.id,
    }));

    if (this.isBoard()) return items;

    return [{ label: this.translate('app.views.groups.no_group'), value: null }, ...items];
  }

  private getSelectedGroup() {
    const groupConfigId = this.groupControl().getRawValue().configId || this.defaultGroupConfigId();

    return this.groups().find(({ id }) => id === groupConfigId) ?? null;
  }

  private defaultGroupConfigId(): string | null {
    if (!this.isBoard()) return null;

    return this.defaultGroup();
  }

  private isBoard(): boolean {
    return this.layoutControl().getRawValue().type === 'board';
  }

  private setHiddenGroups(groups: AnyGroup[]) {
    const { hidden: hiddenGroupsControl } = this.groupControl().controls;
    hiddenGroupsControl.setValue(groups.map(group => group.id));
  }
}
